/* eslint-disable */

/**
 * show_stock tag: per-SKU stock-availability badge + Add-to-Cart gating.
 *
 * Reuses the existing checkout-time inventory-check endpoint
 * (POST {middleware_api_url}/suzu/common/v2/inventory-check) for display purposes,
 * one request per distinct SKU (never a merged multi-SKU batch — see the design spec's
 * "Campaign-bonus cascade" section for why batching breaks per-product attribution).
 *
 * Required globals (already set elsewhere in this theme before this script runs):
 * - window.middleware_api_url
 * - window.sz_config.middleware_key / window.sz_config.timestamp
 * - window.theme.strings.inStock / outOfStock / noStock
 * - window.Shopify.locale
 */
(function () {
  if (window.__stockStatusInitialized) return;
  window.__stockStatusInitialized = true;

  var CONCURRENCY_LIMIT = 5;
  var REQUEST_TIMEOUT_MS = 6000;

  /**
   * Checks stock for a single SKU. Never rejects: every failure mode (network error,
   * timeout, non-2xx, malformed body) resolves to 'unknown' so callers never need
   * their own try/catch.
   * @param {string} sku
   * @returns {Promise<'in_stock'|'out_of_stock'|'unknown'>}
   */
  function fetchSkuStatus(sku) {
    var controller = new AbortController();
    var timeoutId = setTimeout(function () {
      controller.abort();
    }, REQUEST_TIMEOUT_MS);

    return fetch(window.middleware_api_url + '/suzu/common/v2/inventory-check', {
      method: 'POST',
      body: JSON.stringify({
        email: window.customerEmail || '',
        product: [{ sku: sku, qty: 1 }]
      }),
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': Shopify.locale,
        'X-Sig': window.sz_config.middleware_key,
        'X-Epoch': window.sz_config.timestamp
      },
      signal: controller.signal
    })
      .then(function (response) {
        clearTimeout(timeoutId);
        if (!response.ok) return 'unknown';
        return response.json().then(function (body) {
          if (!body || !body.data || !Array.isArray(body.data.out_stocks)) {
            return 'unknown';
          }
          return body.data.out_stocks.length > 0 ? 'out_of_stock' : 'in_stock';
        });
      })
      .catch(function () {
        clearTimeout(timeoutId);
        return 'unknown';
      });
  }

  /**
   * Runs task-functions with at most `limit` in flight at once. Each task must itself
   * never reject (fetchSkuStatus above always resolves), so no error handling needed here.
   * @param {Array<() => Promise<any>>} tasks
   * @param {number} limit
   * @returns {Promise<any[]>} results in the same order as `tasks`
   */
  function runWithConcurrency(tasks, limit) {
    return new Promise(function (resolve) {
      var results = new Array(tasks.length);
      var nextIndex = 0;
      var completed = 0;

      if (tasks.length === 0) {
        resolve(results);
        return;
      }

      function startNext() {
        var currentIndex = nextIndex;
        nextIndex += 1;
        if (currentIndex >= tasks.length) return;

        tasks[currentIndex]().then(function (result) {
          results[currentIndex] = result;
          completed += 1;
          if (completed === tasks.length) {
            resolve(results);
          } else {
            startNext();
          }
        });
      }

      for (var i = 0; i < Math.min(limit, tasks.length); i += 1) {
        startNext();
      }
    });
  }

  /**
   * @returns {string[]} every distinct SKU referenced by a .js-stock-status element on
   * the page, from its data-sku and (on PDP) data-variant-skus attributes.
   */
  function collectSkus() {
    var skus = new Set();
    document.querySelectorAll('.js-stock-status').forEach(function (el) {
      if (el.dataset.sku) skus.add(el.dataset.sku);
      if (el.dataset.variantSkus) {
        try {
          var variants = JSON.parse(el.dataset.variantSkus);
          variants.forEach(function (v) {
            if (v.sku) skus.add(v.sku);
          });
        } catch (err) {
          // Malformed data-variant-skus is treated the same as no variant data;
          // el.dataset.sku (already added above) is still checked.
        }
      }
    });
    return Array.from(skus);
  }

  /**
   * @param {HTMLElement} el - a .js-stock-status placeholder
   * @param {'in_stock'|'out_of_stock'|'unknown'} status
   */
  function renderBadge(el, status) {
    if (status === 'unknown') {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    el.setAttribute('data-inventory-level', status === 'out_of_stock' ? 'none' : 'normal');
    el.textContent = status === 'out_of_stock' ? theme.strings.outOfStock : theme.strings.inStock;
  }

  /**
   * Additive-only: only ever pushes the Add-to-Cart button toward disabled. Never
   * re-enables it, because assets/variant-picker.js's updateAddToCartButton() already
   * reset disabled/textContent from Shopify's native variant.available before this runs
   * (see "Before You Start" note in the plan for why this is safe).
   * @param {HTMLElement} productInfoEl - the .product-info ancestor (PDP only)
   * @param {'in_stock'|'out_of_stock'|'unknown'} status
   */
  function applyAddToCartState(productInfoEl, status) {
    if (status !== 'out_of_stock') return;
    var btn = productInfoEl.querySelector('[data-show-stock="true"]');
    if (!btn) return;
    btn.disabled = true;
    btn.textContent = theme.strings.noStock;
  }

  function init() {
    var skus = collectSkus();
    if (skus.length === 0) return;

    var tasks = skus.map(function (sku) {
      return function () {
        return fetchSkuStatus(sku);
      };
    });

    runWithConcurrency(tasks, CONCURRENCY_LIMIT).then(function (results) {
      var statusMap = new Map();
      skus.forEach(function (sku, index) {
        statusMap.set(sku, results[index]);
      });

      document.querySelectorAll('.js-stock-status').forEach(function (el) {
        var initialStatus = statusMap.get(el.dataset.sku) || 'unknown';
        renderBadge(el, initialStatus);

        var productInfoEl = el.closest('.product-info');
        if (!productInfoEl) return; // collection card: badge only, no button to gate

        applyAddToCartState(productInfoEl, initialStatus);

        var variantPicker = productInfoEl.querySelector('variant-picker');
        if (!variantPicker) return;

        variantPicker.addEventListener('on:variant:change', function (evt) {
          if (!evt.detail.variant) return;
          var variantStatus = statusMap.get(evt.detail.variant.sku) || 'unknown';
          renderBadge(el, variantStatus);
          applyAddToCartState(productInfoEl, variantStatus);
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
